import { noop } from 'lodash';
import { useRouteMatch } from 'react-router';
import { createGlobalState } from 'react-use';

import { Icd10Code } from '@dreryk/egabinet-icd-dictionary-published-language-api';
import {
   ChronicDiseaseSimpleQuery,
   MedicalInformationCountByTypeQuery,
} from '@dreryk/egabinet-patient-medical-information-frontend-api';
import {
   ChronicDiagnosisDoesNotMatchPatientMedicalInformationFault,
   VisitDiagnosisAlreadyExistsFault,
   VisitDiagnosisCreateCommand,
   VisitDiagnosisUpdateAdditionalInformationCommand,
   VisitNotInUpdatableStatusFault,
} from '@dreryk/egabinet-visit-frontend-api';

import { refetchCqrsQuery, useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';
import { Choice } from '@dreryk/watermelon';

import { visitPaths } from '~libs.desktop.shared.paths';

import { visitDetailsTranslationKeys } from '../../translations';

import { DiagnosisTableBean } from './tabs/diagnoses/DiagnosesFormTab.types';

export const useVisitFormSyncingState = createGlobalState<boolean>(false);

export const useVisitFormSyncing = (): boolean => {
   const [visitFormSyncing] = useVisitFormSyncingState();
   return visitFormSyncing;
};

export const useVisitFormCopyAvailable = (): boolean => {
   return Boolean(useRouteMatch({ path: visitPaths.VISIT_INTERVIEW_PATH }));
};

type VisitDiagnosisCreateFault =
   | VisitDiagnosisAlreadyExistsFault
   | ChronicDiagnosisDoesNotMatchPatientMedicalInformationFault
   | VisitNotInUpdatableStatusFault;

type VisitDiagnosisCreateActionOptions = {
   visitId: number;
};

export const useVisitDiagnosisCreateAction = (
   options: VisitDiagnosisCreateActionOptions
): UseCqrsCommandResult<void, DiagnosisTableBean, VisitDiagnosisCreateFault> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new VisitDiagnosisCreateCommand();
         command.chronic = Choice.withUseDefaultIfNull(parameters.chronic);
         command.additionalInformation = Choice.withUseDefaultIfNull(parameters.additionalInformation);
         command.code = Icd10Code.of(parameters.code);
         command.main = Choice.withUseDefaultIfNull(parameters.main);
         command.visitId = options.visitId;
         return command;
      },
      {
         onSuccess: () => {
            refetchCqrsQuery(MedicalInformationCountByTypeQuery);
         },
         onFault: {
            'egabinet-visit-frontend-api:VisitDiagnosisAlreadyExistsFault': noop,
            'egabinet-visit-frontend-api:ChronicDiagnosisDoesNotMatchPatientMedicalInformationFault': () => {
               refetchCqrsQuery(ChronicDiseaseSimpleQuery);
               return dispatchSnackbarNegativeEvent(visitDetailsTranslationKeys.diagnoses.errors.add);
            },
            'egabinet-visit-frontend-api:VisitNotInUpdatableStatusFault': () => {
               return dispatchSnackbarNegativeEvent(visitDetailsTranslationKeys.diagnoses.errors.add);
            },
         },
      }
   );
};

type VisitDiagnosisUpdateAdditionalInformationActionOptions = {
   visitId: number;
};

export const useVisitDiagnosisUpdateAdditionalInformationAction = (
   options: VisitDiagnosisUpdateAdditionalInformationActionOptions
): UseCqrsCommandResult<void, DiagnosisTableBean> => {
   return useCqrsCommand<void, DiagnosisTableBean>((parameters) => {
      const command = new VisitDiagnosisUpdateAdditionalInformationCommand();

      command.additionalInformation = Choice.withUseDefaultIfNull(parameters.additionalInformation);
      command.code = Icd10Code.of(parameters.code);
      command.visitId = options.visitId;

      return command;
   });
};
